import {
    WebSocketBackendMessage,
    WebSocketFrontendMessage,
    WebSocketFrontendMessageAlertMessage,
    WebSocketFrontendMessageCssReload,
    WebSocketFrontendMessageWebSocketSetId
} from '~/shared/src/utilities/modules/webSocket';

import {
    AppDispatch,
    store
} from '~/frontend/src/store';
import {load as configLoad} from '~/frontend/src/actions/config';
import {load as metaModelLoad} from '~/frontend/src/actions/metamodel';
import {load as messageCatalogLoad} from '~/frontend/src/actions/messagecatalog';
import userReload from '~/frontend/src/actions/user/reloadUser';
import {setAlertMessage} from '~/frontend/src/actions/message/setAlertMessage';
import userReloadPermissions from '~/frontend/src/actions/user/reloadPermissions';
import unsetUser from '~/frontend/src/actions/user/unsetUser';
import {clearAllElusiveData} from '~/frontend/src/actions/elusiveData';


declare global {
    /* eslint-disable no-var, @typescript-eslint/naming-convention, no-underscore-dangle */
    var ws : WebSocket | undefined;
    /* eslint-enable no-var, @typescript-eslint/naming-convention, no-underscore-dangle */
}


const debugColorScheme = {
    error: 'F44336',
    base : 'AFB42B'
};


let startReconnect : boolean                      = true,
    reconnect : boolean                           = false,
    reconnectTimeout : NodeJS.Timeout | undefined = undefined;

const onOpen = ( event : Event ) => {
    const debug = store.getState()?.debugConfig?.webSocketDebug ?? false;

    if ( debug ) {
        console.groupCollapsed( `%c${'%cWebSocket - %cconnection established%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.base};`, 'font-weight: normal;' );
        console.log( '    event', event );
        console.groupEnd();
    }

    startReconnect = true;
    reconnect = false;
};

const onClose = ( event : CloseEvent ) => {
    const debug = store.getState()?.debugConfig?.webSocketDebug ?? false;

    if ( debug && !reconnect ) {
        console.groupCollapsed( `%c${'%cWebSocket - %cconnection closed%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.base};`, 'font-weight: normal;' );
        console.log( '    event', event );
        console.groupEnd();
    }

    global.ws = undefined;

    // start trying to reconnect
    if ( startReconnect ) {
        reconnectTimeout = setTimeout(
            () => {
                const asyncReconnect = async () => {
                    if ( debug ) {
                        console.log( `%c${'%cWebSocket - %creconnect ...%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.base};`, 'font-weight: normal;' );
                    }

                    reconnect = true;

                    webSocket.init();

                    clearTimeout( reconnectTimeout );
                    reconnectTimeout = undefined;
                };

                void asyncReconnect();
            },
            10000
        );
    }
};

const onMessage = ( event : MessageEvent ) => {
    const debug    = store.getState()?.debugConfig?.webSocketDebug ?? false,
          dispatch = store.dispatch as AppDispatch;

    let message : WebSocketFrontendMessage | undefined = undefined;

    if ( debug ) {
        console.groupCollapsed( `%c${'%cWebSocket - %conMessage%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.base};`, 'font-weight: normal;' );
        console.log( '    event', event );
    }

    try {
        message = JSON.parse( String( event.data ) );
    }
    catch ( e ) {
        if ( debug ) {
            console.log( '%cprocessing aborted due to error parsing webSocket message', `color: #${debugColorScheme.error}; font-weight: bold;` );
            console.error( e );
        }
    }

    if ( message ) {
        if ( debug ) {
            console.log( '    message', message );
        }

        const processCommand = async () => {
            let fileRef,
                userId;

            // process message
            switch ( message!.command ) {
                case 'websocket:noSession':
                    userId = global.user ? global.user.userId : '';

                    dispatch( unsetUser() );
                    dispatch( clearAllElusiveData() );

                    if ( userId ) {
                        // show logged out message in login dialog
                        global.loginAfterSessionExpired = true;

                        localStorage.setItem( 'lastUser', userId );

                        // don't start reconnect at close
                        startReconnect = false;
                    }
                    break;

                case 'websocket:setId':
                    global.webSocketId = ( message as WebSocketFrontendMessageWebSocketSetId )!.parameters.id;
                    break;

                case 'appconfig:reload':
                    await dispatch( configLoad() );
                    break;

                case 'messagecatalog:reload':
                    await dispatch( messageCatalogLoad() );
                    break;

                case 'metamodel:reload':
                    await dispatch( userReloadPermissions() );
                    await dispatch( metaModelLoad() );
                    await dispatch( messageCatalogLoad() );
                    await dispatch( userReload() );
                    break;

                case 'css:reload':
                    fileRef = document.createElement( 'link' );

                    if ( fileRef ) {
                        fileRef.setAttribute( 'rel', 'stylesheet' );
                        fileRef.setAttribute( 'href', `${BASE_URL}/css/${( message as WebSocketFrontendMessageCssReload )!.parameters.name}?tmp=${String.getHash()}` );
                        document.getElementsByTagName( 'head' )[ 0 ].appendChild( fileRef );
                    }
                    break;

                case 'user:reload':
                    await dispatch( userReload() );
                    break;

                case 'message:alert':
                    dispatch( setAlertMessage( ( message as WebSocketFrontendMessageAlertMessage )!.parameters ) );
                    break;

                case 'message:chat':
                    // TODO: implement -> see backend + chat panel
                    break;
            }
        };

        void processCommand();
    }

    console.groupEnd();
};

const onError = ( event : Event ) => {
    const debug = store.getState()?.debugConfig?.webSocketDebug ?? false;

    if ( debug && !reconnect ) {
        console.groupCollapsed( `%c${'%cWebSocket - %conError%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.error};`, 'font-weight: normal;' );
        console.log( '    event', event );
        console.groupEnd();
    }
};

const webSocket = {
    init() {
        const debug = store.getState()?.debugConfig?.webSocketDebug ?? false;

        if ( debug && !reconnect ) {
            console.log( `%c${'%cWebSocket - %cconnect ...%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.base};`, 'font-weight: normal;' );
        }

        global.ws = new WebSocket( `${SOCKET_URL}/websockets` );

        global.ws.addEventListener( 'open', onOpen );
        global.ws.addEventListener( 'close', onClose );
        global.ws.addEventListener( 'message', onMessage );
        global.ws.addEventListener( 'error', onError );
    },

    send( message : WebSocketBackendMessage ) {
        global.ws?.send( JSON.stringify( message ) );
    }
};

export default webSocket;
